import { ClipboardButton, RadioGroup, AkahuPoweredBy, AkahuSupportedBanks } from "@mixitone/components";
import { Option } from "@mixitone/components/Controls/Form/RadioGroup";
import { TournamentPaymentMethod } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import {
  AKAHU_ABOUT,
  AKAHU_METHOD_DESCRIPTION,
  AKAHU_METHOD_NAME,
  AKAHU_METHOD_ONEOFF_NOTE,
  AKAHU_PAY_BY_BANK_BANKS,
  AKAHU_PLAYER_LIMITED_BANKS,
  formatAkahuPlayerAmount,
  getPaymentMethod,
  type AkahuSupportedBank,
} from "@mixitone/payments";
import { formatSegmented } from "@mixitone/util";
import { ReactNode, useEffect, useState } from "react";
import portalFunctions from "lib/portalFunctions";
import { CompetitionRegistrationPageController } from "../controllers/CompetitionRegistrationPageController";

/**
 * Props for payment method components
 */
interface PaymentMethodProps {
  method: TournamentPaymentMethod;
  registrationComplete: boolean;
  paymentReference?: string;
}

/**
 * Component for displaying NZ bank payment method details
 */
const NzBankPaymentMethod = ({ method, paymentReference }: PaymentMethodProps) => {
  const config = method.configuration;
  const paymentMethod = getPaymentMethod(method.payment_type!) as any;

  return (
    <div className="rounded border border-gray-300 bg-gray-50 p-4">
      <p>{method.description}</p>
      <div className="mt-2 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 text-sm text-gray-600">
        <div>Amount:</div>
        <div className="col-span-2">${config?.amount}</div>
        <div>Account Name:</div>
        <div className="min-w-0 break-words">{config?.accountName}</div>
        <div>
          <ClipboardButton text={config?.accountName} />
        </div>
        <div>Account Number:</div>
        <div className="min-w-0 break-words">
          {formatSegmented(config?.accountNumber, paymentMethod.format)}
        </div>
        <div>
          <ClipboardButton text={formatSegmented(config?.accountNumber, paymentMethod.format)} />
        </div>
        <div>Reference:</div>
        <div className="min-w-0 break-words">{paymentReference}</div>
        <div>
          <ClipboardButton text={paymentReference!} />
        </div>
      </div>
      <p className="mt-2 text-sm text-gray-600">
        Transfer the amount using the details above, then click next. The club will confirm when your payment
        is received.
      </p>
    </div>
  );
};

/**
 * Component for displaying cash payment method details
 */
const CashPaymentMethod = ({ method }: PaymentMethodProps) => {
  const config = method.configuration;

  return (
    <div className="rounded border border-gray-300 bg-gray-50 p-4">
      <p>{method.description}</p>
      <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm text-gray-600">
        <div>Amount:</div>
        <div>${config.amount}</div>
        {method.instructions && (
          <>
            <div>Instructions:</div>
            <p className="min-w-0 break-words">{method.instructions}</p>
          </>
        )}
      </div>
    </div>
  );
};

const AkahuOneOffPaymentMethod = ({ method }: PaymentMethodProps) => {
  const controller = CompetitionRegistrationPageController.use();
  const { akahuPolling, akahuAwaitingConfirmation, akahuStatusMessage, akahuStatusIsError } =
    controller.state;
  const isPaid = controller.state.registration?.player.isPaid;
  const amount = formatAkahuPlayerAmount(method.configuration?.amount);
  const [banks, setBanks] = useState<AkahuSupportedBank[]>(AKAHU_PAY_BY_BANK_BANKS);
  const [loadingBanks, setLoadingBanks] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingBanks(true);
    void portalFunctions
      .listAkahuSupportedBanks({})
      .then((response) => {
        if (!cancelled && response.banks?.length) {
          setBanks(response.banks);
        }
      })
      .catch(() => {
        // Keep official fallback names.
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingBanks(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (isPaid) {
    return (
      <div className="rounded border border-green-300 bg-green-50 p-4 text-green-800">
        <p className="font-medium">Payment received</p>
        <p className="mt-1 text-sm">Your bank payment has been confirmed.</p>
      </div>
    );
  }

  const paymentLocked = controller.akahuPaymentLocked;
  const inProgress =
    akahuPolling || akahuAwaitingConfirmation || (!!akahuStatusMessage && !akahuStatusIsError);
  const retryableError = !!akahuStatusMessage && akahuStatusIsError && !paymentLocked;

  const payByBankInfo = (
    <div className="rounded border border-gray-300 bg-gray-50 p-4 space-y-3">
      <p>{AKAHU_METHOD_DESCRIPTION}</p>
      {amount && (
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <div className="text-sm text-gray-600">Amount:</div>
          <div className="font-semibold text-gray-900">${amount} NZD</div>
        </div>
      )}
      <p className="text-sm text-gray-600">{AKAHU_METHOD_ONEOFF_NOTE(amount)}</p>
      <AkahuSupportedBanks
        banks={banks}
        loading={loadingBanks}
        compact
        description={AKAHU_PLAYER_LIMITED_BANKS}
      />
      <AkahuPoweredBy className="flex items-start gap-3 text-xs text-gray-600">{AKAHU_ABOUT}</AkahuPoweredBy>
    </div>
  );

  if (inProgress) {
    return (
      <div className="rounded border border-blue-300 bg-blue-50 p-4 text-blue-900">
        {akahuPolling && (
          <p className="font-medium">
            {akahuStatusMessage?.includes("Redirecting")
              ? "Redirecting to your bank..."
              : "Confirming your payment..."}
          </p>
        )}
        {akahuAwaitingConfirmation && !akahuPolling && (
          <p className="font-medium">Waiting for bank confirmation</p>
        )}
        <p className={akahuPolling || akahuAwaitingConfirmation ? "mt-1 text-sm" : "text-sm"}>
          {akahuStatusMessage || "Please wait while we confirm payment status. Do not submit payment again."}
        </p>
        {akahuPolling && !akahuStatusMessage?.includes("Still confirming") && (
          <p className="mt-2 text-sm opacity-80">This usually takes a few seconds.</p>
        )}
        {akahuAwaitingConfirmation && !akahuPolling && (
          <button
            type="button"
            className="mt-3 text-sm font-medium underline underline-offset-2"
            onClick={() => controller.actionCheckAkahuPaymentStatus()}
          >
            Check payment status
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {retryableError && (
        <div className="rounded border border-amber-300 bg-amber-50 p-4 text-amber-900">
          <p className="text-sm">{akahuStatusMessage}</p>
        </div>
      )}
      {payByBankInfo}
    </div>
  );
};

const paymentMethodComponents = {
  "nz-bank": NzBankPaymentMethod,
  cash: CashPaymentMethod,
  "akahu-one-off": AkahuOneOffPaymentMethod,
} as const;

const PaymentSelection = () => {
  const controller = CompetitionRegistrationPageController.use();
  const {
    tournament,
    registrationComplete,
    selectedPaymentMethodId,
    registration,
    partnerCode,
    akahuPolling,
    akahuAwaitingConfirmation,
  } = controller.state;
  const { isDoubles } = controller;

  if (!tournament || !tournament.paymentMethods.loaded) {
    return null;
  }

  const paymentMethods = tournament.paymentMethods.filter(
    (method) => method.is_enabled && !method.is_deleted,
  );

  if (paymentMethods.length === 0) {
    return null;
  }

  const isPaid = registration?.player.isPaid;
  const paymentLocked = isPaid || controller.akahuPaymentLocked;
  const selectedMethod = paymentMethods.find((method) => method.id === selectedPaymentMethodId) ?? null;
  const akahuStatusVisible =
    akahuPolling || akahuAwaitingConfirmation || !!controller.state.akahuStatusMessage;
  // In-progress and retryable-error copy already render above the radios for pay-by-bank.
  const showSelectedMethodDetails =
    !!selectedMethod &&
    !controller.akahuPaymentLocked &&
    !(akahuStatusVisible && selectedMethod.payment_type === "akahu-one-off");

  const renderPaymentMethodComponent = (method: TournamentPaymentMethod): ReactNode => {
    const paymentReference = controller.state.registration?.player?.payment_reference;
    const Component = paymentMethodComponents[method.payment_type as keyof typeof paymentMethodComponents];
    if (!Component) {
      return null;
    }

    return (
      <Component
        method={method}
        registrationComplete={registrationComplete}
        paymentReference={paymentReference}
      />
    );
  };

  if (partnerCode && isDoubles && tournament.payment_by_player_or_entry === "entry") {
    return (
      <div className="rounded border border-gray-300 bg-green-50 p-4 text-green-800">
        Payment is per entry. As a doubles partner, you are not required to make a payment for this
        competition.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isDoubles && tournament.payment_by_player_or_entry === "entry" && (
        <div className="mt-2 text-sm text-gray-600">
          Payment is per entry, your partner will not be charged.
        </div>
      )}

      {(akahuPolling || akahuAwaitingConfirmation || controller.state.akahuStatusMessage) && !isPaid && (
        <AkahuOneOffPaymentMethod
          method={
            paymentMethods.find((method) => method.id === selectedPaymentMethodId) || paymentMethods[0]!
          }
          registrationComplete={registrationComplete}
        />
      )}

      <RadioGroup
        name="payment_method"
        value={selectedPaymentMethodId || ""}
        onChange={(value) => controller.actionSetSelectedPaymentMethod(value as string)}
        disabled={paymentLocked}
      >
        {paymentMethods.map((method) => (
          <Option
            key={method.id}
            value={method.id!}
            title={() =>
              method.payment_type === "akahu-one-off"
                ? AKAHU_METHOD_NAME
                : method.description || method.payment_type
            }
          />
        ))}
      </RadioGroup>

      {showSelectedMethodDetails && (
        <div className="mt-4">{renderPaymentMethodComponent(selectedMethod!)}</div>
      )}
    </div>
  );
};

export default ApplicationView(PaymentSelection);
